import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useSettingsQueryApi } from 'graphql/generated';
import { isB2bDomain } from 'helpers/isDomainLocale';
import { replaceAll } from 'helpers/replaceAll';
import bigDecimal from 'js-big-decimal';
import useTranslation from 'next-translate/useTranslation';
import { PriceType, ProductPriceType } from 'types/price';
import { DomainConfigType } from 'utils/Domain/Domain';
import { formatPrice } from 'utils/formatting';

export type ProductPrice =
    | ProductPriceType
    | PriceType
    | { priceWithVat: number; priceWithoutVat: number; vatAmount: number };

type FormatOptions = {
    minimumFractionDigits?: number;
    explicitZero?: boolean;
};

type FormatPriceFunctionType = (price: number, options?: FormatOptions) => string;
type FormatPriceObjectFunctionType = (price: ProductPrice, options?: FormatOptions) => string;

export const getSellingPriceByDomain = (price: ProductPrice, domainConfig: DomainConfigType) => {
    if (isB2bDomain(domainConfig)) {
        return price.priceWithoutVat;
    }

    return price.priceWithVat;
};

export const formatSaleAmountPercent = (saleAmountPercent: bigDecimal) => {
    return saleAmountPercent.round(1, bigDecimal.RoundingModes.FLOOR).getValue();
};

export const useFormatPrice = (): {
    formatPriceNumber: FormatPriceFunctionType;
    formatPriceObject: FormatPriceObjectFunctionType;
    formatPriceObjectForSR: FormatPriceObjectFunctionType;
} => {
    const { t } = useTranslation();
    const domainConfig = useDomainConfig();
    const [{ data }] = useSettingsQueryApi({ requestPolicy: 'cache-first' });
    const {
        minimumFractionDigits: domainSettingMinimumFractionDigits,
        defaultCurrencyCode: domainSettingCurrencyCode,
    } = data?.settings?.pricing ?? {};

    const defaultOptions = { explicitZero: isB2bDomain(domainConfig) };

    const formatPriceNumber = (price: number, options?: FormatOptions) =>
        formatPrice(
            price,
            domainSettingCurrencyCode ?? domainConfig.currencyCode,
            t,
            domainConfig.defaultLocale,
            options?.minimumFractionDigits ??
                domainSettingMinimumFractionDigits ??
                domainConfig.defaultMinimumFractionDigits,
            {
                ...defaultOptions,
                ...options,
            },
        );

    const formatPriceObjectForSR = (price: ProductPrice, options?: FormatOptions) => {
        const formattedPrice = formatPriceNumber(getSellingPriceByDomain(price, domainConfig), {
            ...defaultOptions,
            ...options,
        });

        const priceWithoutThousandsSeparators = formattedPrice.replace(/(\d)\u00A0(?=\d)/g, '$1');

        const priceWithDotDecimal = replaceAll(priceWithoutThousandsSeparators, ',', '.');

        return priceWithDotDecimal;
    };

    return {
        formatPriceNumber: formatPriceNumber,
        formatPriceObject: (price: ProductPrice, options?: FormatOptions) =>
            formatPriceNumber(getSellingPriceByDomain(price, domainConfig), { ...defaultOptions, ...options }),
        formatPriceObjectForSR: formatPriceObjectForSR,
    };
};
